import React, { Fragment, ReactNode } from 'react';
import { Box, Breadcrumbs, IconButton, Tooltip } from '@mui/material';
import {
  UnfoldLess as UnfoldLessIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';
import { Link, useLocation, useParams } from 'react-router-dom';
import { isPathEqualToRoute, pagePaths } from 'routes/paths';
import { useAppSelector } from 'app/hooks';
import { usePageIntro } from 'components/PageIntro/PageIntroContext';

interface BreadCrumbExtraItem {
  label: string;
  onClick?: () => void;
  to?: string;
}

// A resolved crumb passed to react-router's <Link>. `onClick` is optional: the
// trailing crumb may carry `currentOnClick`, and extra items can supply their
// own handler — plain path crumbs have neither.
interface BreadCrumbLink {
  to: string;
  children: string;
  onClick?: () => void;
}

interface IBreadCrumbsProps {
  currentLabel?: string;
  currentOnClick?: () => void;
  extraItems?: BreadCrumbExtraItem[];
  /** Right-side action cluster rendered inside the breadcrumb row. */
  actions?: ReactNode;
}

const BreadCrumbs: React.FC<IBreadCrumbsProps> = ({
  currentLabel,
  currentOnClick,
  extraItems = [],
  actions,
}) => {
  const { pathname } = useLocation();
  const {
    categoryName = '',
    subcategoryName = '',
    wordSetType = '',
  } = useParams();
  const vocabulary = useAppSelector((store) => store.common.vocabulary.data);

  const linkNames: Record<keyof typeof pagePaths, string> = {
    index: 'Головна',
    wordset: 'Набори слів',
    levelBased: 'На основі рівня',
    topicRelated: 'Тематична англійська лексика',
    mostCommon: 'Найбільш поширені слова',
    irregularVerb: 'Неправильні дієслова',
    vocabulary: 'Словник',
    categoryOverview: categoryName,
    subCategoryOverview: subcategoryName,
    wordSetCategoryOverview: wordSetType,
    vocabularyWords: vocabulary[0]?.vocabularyName || '',
    vocabularyWordsPage: '',
    vocabularyExpressions: vocabulary[2]?.vocabularyName || '',
    vocabularyExpressionsPage: '',
    vocabularyVerbs: vocabulary[1]?.vocabularyName || '',
    vocabularyExport: 'Експорт слів',
    vocabularyImport: 'Імпорт слів',
    vocabularyTest: 'Тест словникового запасу',
    // The active run supplies its own dynamic leaf crumb (the test's start
    // moment) via `extraItems`, so the static route carries no crumb of its own.
    vocabularyTestRun: '',
    vocabularyTestResult: 'Результати тестування',
    learning: 'Вправи на вивчення',
    learningSession: 'Сесія вправ',
    inAction: 'В дії',
    inActionList: 'Список слів',
    community: 'Спільнота',
    communityInActionSets: 'Набори з «В дії»',
    profile: 'Мій профіль',
    achievements: 'Нагороди',
    // Feedback pages render no breadcrumb trail, so these routes never surface
    // a crumb — kept here only to satisfy the exhaustive route map.
    feedbackIdeas: '',
    feedbackMine: '',
    adminFeedback: '',
  };

  const links = [
    ...[pagePaths.index]
      .concat(
        pathname
          .slice(Number(pathname[0] === '/'))
          .split('/')
          .filter((text) => !!text)
          .map((_, index, arr) => arr.slice(0, index + 1).join('/')),
      )
      .map((path): BreadCrumbLink => {
        const [name] =
          Object.entries(pagePaths).find(([_, url]) =>
            isPathEqualToRoute(path, url),
          ) || [];

        return {
          to: '/' + path,
          children: linkNames[name as keyof typeof pagePaths] || '',
        };
      })
      .filter((link) => !!link.children),
  ];
  const resolvedLinks = links.map((link, index, arr) =>
    currentLabel && index === arr.length - 1
      ? {
          ...link,
          children: currentLabel,
          onClick: currentOnClick,
        }
      : link,
  );
  const breadcrumbItems: BreadCrumbLink[] = [
    ...resolvedLinks,
    ...extraItems
      .filter(({ label }) => !!label)
      .map(({ label, onClick, to }) => ({
        children: label,
        onClick,
        to: to || pathname,
      })),
  ];

  return (
    <PageIntroToggleRow actions={actions}>
      <Breadcrumbs
        separator={'/'}
        aria-label="breadcrumb"
        sx={{
          'a, li:not(:last-of-type)': {
            color: 'primary.breadcrumbsLink',
          },
        }}
      >
        {breadcrumbItems.map((link, index, arr) => (
          <Fragment key={`${link.to}-${index}`}>
            {index !== arr.length - 1 || link.onClick ? (
              <Link {...link} />
            ) : (
              link.children
            )}
          </Fragment>
        ))}
      </Breadcrumbs>
    </PageIntroToggleRow>
  );
};

// Pairs the breadcrumb trail with (1) the page-intro toggle attached to the
// trail and (2) optional page-level action buttons pushed to the far right.
// Reads the PageIntro context at render time so the toggle only shows when a
// PageIntro on the current page has registered itself.
const PageIntroToggleRow: React.FC<{
  children: ReactNode;
  actions?: ReactNode;
}> = ({ children, actions }) => {
  const { collapsed, toggle, hasIntro } = usePageIntro();

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1.25,
        mt: 1.5,
        minHeight: 32,
      }}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>{children}</Box>
        {hasIntro && (
          <Tooltip
            title={collapsed ? 'Показати опис сторінки' : 'Сховати опис сторінки'}
          >
            <IconButton
              aria-label={
                collapsed ? 'Показати опис сторінки' : 'Сховати опис сторінки'
              }
              aria-expanded={!collapsed}
              onClick={toggle}
              size={'small'}
              sx={{ color: 'text.secondary', flex: 'none' }}
            >
              {collapsed ? (
                <UnfoldMoreIcon sx={{ fontSize: 20 }} />
              ) : (
                <UnfoldLessIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {actions && (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.25,
            ml: 'auto',
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );
};

export default BreadCrumbs;
